/**
 * @module web/playerManager
 * @private
 */

let heartbeatData = null;
let sdk = null;
let payload = null;
let heartbeatUpdateTimer = null;
let newEpisode = null;
let onlyOnFirstPlay = true;

const heartbeatUpdater = () => {
  if (heartbeatData) {
    heartbeatUpdateTimer = heartbeatUpdateTimer || setInterval(sendHeartbeat, heartbeatData.heartbeatPeriod);
    sendHeartbeat();
  }
};

const sendAndStopHeartbeat = () => {
  haltHeartbeat();
  sendHeartbeat();
};

/**
 * Sets up the configuration for the heartbeat calls during the playback. This involves two steps, gathering the data
 * about the content being played in order to include it on the subsequent calls and preparing the events and timers
 * needed to periodically and when different events happen in the player.
 * @param {Object} playbackData Object holding information that needs to be sent on each heartbeat or is needed to
 * configure the heartbeat itself. Expects the following properties:
 *  - {Number|null} heartbeatPeriod: if it's a number, it will be the period in milliseconds to wait between heartbeat
 *  and heartbeat. If null, the entire heartbeat feature will be disabled.
 *  - {String} id: Id of the content being played. It will be either the movie ID or the episode ID (never series ID).
 *  - {String} seriesId: If an episode is being played, this property will hold the series ID.
 *  - {String} moduleId: For recommendation engine. It's the ID of the carousel that contained the poster that generated the
 *  playback.
 *  - {String} pageLink: For recommendation engine.
 *  - {Number} seasonNumber: If an episode is being played, the season number it belongs to.
 *  - {Number} episodeNumber: If an episode is being played, the episode number it has within the season it belongs to.
 * @param {Object} sdkInstance  An instance of the SDK to allow this module to access mediaManager and playerManager
 * without having to deal with platform specific implementation details.
 * @param {Boolean} isNewEpisode Indicates if the initialization is made by a new episode
 */
function initialize(playbackData, sdkInstance, isNewEpisode) {
  if (playbackData && playbackData.heartbeatPeriod) {
    newEpisode = isNewEpisode;
    heartbeatData = playbackData;
    sdk = sdkInstance;
    generatePayload();
    // FIXME: Note the problem could be in the comments below.
    sdk.playerManager.on("play", heartbeatUpdater);
    sdk.playerManager.on("pause", sendAndStopHeartbeat); // CHANGED: sendHeartbeat to sendAndStopHeartbeat
    sdk.playerManager.on("willStop", sendHeartbeat);
    // sdk.playerManager.on("seek", sendHeartbeat); // REMOVED
    sdk.playerManager.on("stop", stopHeartbeat);
    sdk.playerManager.on("destroy", stopHeartbeat);
    sdk.playerManager.on("error", stopHeartbeat);

    // This starts the periodic heartbeat with 1 second of delay. This is needed due to a limitation of the Tizen
    // player that returns an invalid playback position when the player has just started (it returns incorrect values).
    // FIXME: Note this could be fixed as "didPlayerProgress" property is checked in WBIGS currently.
    setTimeout(heartbeatUpdater, 1000);
  }
}

/**
 * Creates an object containing the data that is constant across heartbeat requests. These properties are origin,
 * media_id, series_id, module_id, page_link, season_number, episode_number. Not all are required in all the cases.
 * @returns {Object} The set of constant values required by the heartbeat endpoint.
 */
async function generatePayload() {
  const platform = await sdk.systemManager.device.getPlatform();
  const year = await sdk.systemManager.device.getYear();
  payload = {
    origin: platform + year,
    media_id: heartbeatData.id
  };

  if (heartbeatData.seriesId) {
    payload.series_id = heartbeatData.seriesId;
  }
  if (heartbeatData.moduleId) {
    payload.module_id = heartbeatData.moduleId;
  }
  if (heartbeatData.pageLink) {
    payload.page_link = heartbeatData.pageLink;
  }
  if (heartbeatData.seasonNumber) {
    payload.season_number = heartbeatData.seasonNumber;
  }
  if (heartbeatData.episodeNumber) {
    payload.episode_number = heartbeatData.episodeNumber;
  }
}

/**
 * Completely stops the heartbeat process by killing the timers and removing the data of the content being played.
 */
function stopHeartbeat() {
  reset();
  sdk.playerManager.off("play", heartbeatUpdater);
  sdk.playerManager.off("pause", sendAndStopHeartbeat);
  sdk.playerManager.off("willStop", sendHeartbeat);
  sdk.playerManager.off("stop", stopHeartbeat);
  sdk.playerManager.off("destroy", stopHeartbeat);
  sdk.playerManager.off("error", stopHeartbeat);
}

/**
 *  Performs a call to the backend by including the heartbeat payload with data related to the content being played and
 *  the playback progress.
 */
function sendHeartbeat() {
  let currentTime = sdk.playerManager.getCurrentTime();
  let currentTimeInSeconds = newEpisode ? 0 : currentTime ? currentTime / 1000 : 0;
  newEpisode = null;
  if (heartbeatData.isFirstPlay) {
    payload.first_play = onlyOnFirstPlay ? heartbeatData.isFirstPlay : false;
    onlyOnFirstPlay = false;
  }
  payload.playback_time = Math.trunc(currentTimeInSeconds).toString();
  sdk.mediaManager.heartBeat(payload);
}

function haltHeartbeat() {
  clearInterval(heartbeatUpdateTimer);
  heartbeatUpdateTimer = null;
}

function reset() {
  heartbeatData = null;
  payload = null;
  onlyOnFirstPlay = true;
  haltHeartbeat();
}

export default {
  initialize,
  reset
};
